import { flags } from '../openfeature/flags'
import { getPageType } from '../composables/analytics/helper'

/**
 * Dev Debug Panel
 *
 * Mounts the shared `<dev-debug-panel>` Web Component
 * (`@redbrickmedia/duplex.dev-debug-panel`) once per page. The component
 * self-registers on import and reads diagnostics off `window` globals
 * (`pageType`, `googletag`, `assertive`, `ayManagerEnv`, `dataLayer`,
 * `_featureFlags`), so this plugin's only jobs are:
 *
 *   1. Gate visibility to dev OR `localStorage['app::debug'] === 'true'`.
 *   2. Snapshot the evaluated feature-flag values into `window._featureFlags`
 *      and the current page type into `window.pageType` so the panel's tabs
 *      have data to render.
 *
 * Flag keys come from the in-repo `flags` registry; each is evaluated live
 * through the OpenFeature client so the panel reflects the values this visitor
 * actually resolves (targeting, split tests, and query overrides included).
 *
 * Runs after `openfeature` (client must be ready) and the ad/GTM plugins, so
 * the ad-stack globals it reads are already populated.
 */

const DEBUG_LOCAL_STORAGE_KEY = 'app::debug'

function isDebugEnabled(): boolean {
  if (import.meta.dev) return true
  try {
    return window.localStorage.getItem(DEBUG_LOCAL_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export default defineNuxtPlugin({
  name: 'dev-debug-panel',
  dependsOn: ['openfeature'],
  setup: async () => {
    if (!isDebugEnabled()) return

    const { client } = await useOpenFeature()

    // Enumerate the in-repo flag registry and evaluate each key live. The
    // registry's `defaultValue` is a primitive union, so narrow on its runtime
    // type to pick the matching evaluation method (no type assertions).
    const snapshot: Record<string, boolean | string | number> = {}
    for (const [key, definition] of Object.entries(flags)) {
      const fallback = definition.defaultValue
      if (typeof fallback === 'boolean') {
        snapshot[key] = client.getBooleanValue(key, fallback)
      } else if (typeof fallback === 'number') {
        snapshot[key] = client.getNumberValue(key, fallback)
      } else {
        snapshot[key] = client.getStringValue(key, fallback)
      }
    }
    window._featureFlags = snapshot

    // Keep the page type fresh across SPA navigation.
    const syncPageType = () => {
      window.pageType = getPageType()
    }
    syncPageType()
    useRouter().afterEach(syncPageType)

    // Side-effect import registers the <dev-debug-panel> custom element.
    await import('@redbrickmedia/duplex.dev-debug-panel')

    if (!document.querySelector('dev-debug-panel')) {
      document.body.appendChild(document.createElement('dev-debug-panel'))
    }
  },
})
